<template>
  <div class="suivi-recommandations">
    <div class="header">
      <div class="controls">
        <select v-model="filterStatus" @change="applyFilters" class="status-filter">
          <option value="all">Toutes</option>
          <option value="en_attente">En attente</option>
          <option value="en_cours">En cours</option>
          <option value="terminee">Terminées</option>
        </select>
        <button @click="printReport" class="print-btn">
          <i class="fas fa-print"></i> Imprimer le rapport
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-indicator">
      <i class="fas fa-spinner fa-spin"></i> Chargement en cours...
    </div>

    <div v-if="errorMessage" class="error-message">
      <i class="fas fa-exclamation-triangle"></i> {{ errorMessage }}
    </div>

    <div class="stats-summary">
      <div class="stat-card">
        <div class="stat-value">{{ totalRecommendations }}</div>
        <div class="stat-label">Recommandations</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ completedPercentage }}%</div>
        <div class="stat-label">Actions terminées</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ totalActionsEnRetard }}</div>
        <div class="stat-label">Actions en retard</div>
      </div>
    </div>

    <div class="recommendations-container">
      <div v-if="filteredItems.length === 0" class="no-results">
        Aucune recommandation trouvée pour ce filtre.
      </div>

      <div class="recommendation-item" 
           v-for="item in filteredItems" 
           :key="item.recommandation.id"
           :class="['status-' + item.recommandation.statut, { 'en-retard': item.recommandation.est_en_retard }]">
        <div class="item-header">
          <h3>{{ item.recommandation.titre }}</h3>
          <div class="header-right">
            <span v-if="item.recommandation.priorite" class="priority-badge">{{ item.recommandation.priorite }}</span>
            <span class="status-badge">{{ getStatusText(item.recommandation.statut) }}</span>
          </div>
        </div>
        
        <div class="item-content">
          <p class="description">{{ item.recommandation.description }}</p>
          
          <div class="item-meta">
            <div class="meta-row">
              <span class="meta-label">Date limite :</span>
              <span class="meta-value" :class="{ 'date-late': item.recommandation.est_en_retard }">
                {{ formatDate(item.recommandation.date_echeance) }}
                <i v-if="item.recommandation.est_en_retard" class="fas fa-exclamation-circle"></i>
              </span>
            </div>
            
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-number">{{ item.statistiques?.total?.actions ?? 0 }}</div>
                <div class="stat-label">Actions totales</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ item.statistiques?.total?.terminees ?? 0 }}</div>
                <div class="stat-label">Terminées</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ item.statistiques?.total?.en_retard ?? 0 }}</div>
                <div class="stat-label">En retard</div>
              </div>
            </div>
          </div>
        </div>

        <div class="item-actions">
          <button @click="showDetails(item)" class="action-btn">
            <i class="fas fa-info-circle"></i> Détails
          </button>
          <button @click="markAsComplete(item)" 
                    v-if="item.recommandation?.statut !== 'terminee'" 
                    class="action-btn complete-btn">
              Terminer
            </button>
        </div>
      </div>
    </div>

    <!-- Modal de détails -->
    <div v-if="selectedItem" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ selectedItem.recommandation.titre }}</h3>
          <button @click="closeModal" class="close-btn">&times;</button>
        </div>
        
        <div class="modal-body">
          <div class="section">
            <h4><i class="fas fa-info-circle"></i> Description</h4>
            <p>{{ selectedItem.recommandation.description }}</p>
            
            <div class="recommandation-meta">
              <div class="meta-item">
                <span class="meta-label">Statut :</span>
                <span class="meta-value">{{ getStatusText(selectedItem.recommandation.statut) }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">Date limite :</span>
                <span class="meta-value">{{ formatFullDate(selectedItem.recommandation.date_echeance) }}</span>
              </div>
              <!-- <div class="meta-item">
                <span class="meta-label">Créée le :</span>
                <span class="meta-value">{{ formatFullDate(selectedItem.recommandation.date_creation) }}</span>
              </div> -->
            </div>
          </div>
          
          <!-- Actions du client -->
          <div class="section">
            <h4>Actions du client</h4>
            <div v-if="!(selectedItem.actions?.clients?.length)" class="no-actions">
              Aucune action client pour cette recommandation
            </div>
            <div v-for="action in (selectedItem.actions?.clients || [])" :key="action.id" class="action-item">
              <div class="action-header">
                <h5>{{ action.description }}</h5>
                <span :class="['status-badge', action.statut]">{{ getStatusText(action.statut) }}</span>
              </div>
              <div class="action-meta">
                <span><i class="far fa-calendar-alt"></i> Échéance: {{ formatFullDate(action.date_echeance) }}</span>
                <span v-if="action.est_en_retard" class="late-indicator">
                  <i class="fas fa-exclamation-triangle"></i> En retard
                </span>
              </div>
            </div>
          </div>

          <div class="section">
            <h4> Actions des employés</h4>
            <div v-if="!(selectedItem.actions?.employes?.length)" class="no-actions">
              Aucune action employé pour cette recommandation
            </div>
            <div v-for="action in (selectedItem.actions?.employes || [])" :key="action.id" class="action-item">
              <div class="action-header">
                <h5>{{ action.description }}</h5>
                <span :class="['status-badge', action.statut]">{{ getStatusText(action.statut) }}</span>
              </div>
              <div class="action-meta">
                <span><i class="far fa-calendar-alt"></i> Échéance: {{ formatFullDate(action.date_echeance) }}</span>
                <span v-if="action.est_en_retard" class="late-indicator">
                  <i class="fas fa-exclamation-triangle"></i> En retard
                </span>
              </div>
              <div class="raci-roles" v-if="action.responsable || action.accountable">
                <span v-if="action.responsable"><strong>Responsable:</strong> {{ action.responsable.prenom }} {{ action.responsable.nom }}</span>
                <span v-if="action.accountable"><strong>Accountable:</strong> {{ action.accountable.prenom }} {{ action.accountable.nom }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import '@/assets/css/suivirecommandations.css'
import { isAuthenticatedSession } from '@/utils/sessionAuth';

export default {
  data() {
    return {
      filterStatus: 'all',
      selectedItem: null,
      recommendations: [],
      loading: false,
      errorMessage: null
    }
  },
  computed: {
    filteredItems() {
      if (this.filterStatus === 'all') return this.recommendations;
      return this.recommendations.filter(item => 
        item.recommandation.statut === this.filterStatus
      );
    },
    totalRecommendations() {
      return this.recommendations.length;
    },
    completedPercentage() {
      const totalActions = this.recommendations.reduce((sum, item) => 
        sum + (item.statistiques?.total?.actions ?? 0), 0);
      const completedActions = this.recommendations.reduce((sum, item) => 
        sum + (item.statistiques?.total?.terminees ?? 0), 0);
      return totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0;
    },
    totalActionsEnRetard() {
      return this.recommendations.reduce((sum, item) => 
        sum + (item.statistiques?.total?.en_retard ?? 0), 0);
    }
  },
  created() {
    this.fetchRecommendations();
  },
  methods: {
    async fetchRecommendations() {
      this.loading = true;
      this.errorMessage = null;
      
      try {
        if (!isAuthenticatedSession()) {
          this.errorMessage = "Token non trouvé, veuillez vous connecter.";
          this.$router.push('/connexion');
          return;
        }

        const response = await axios.get("/api/raci/", {
          withCredentials: true
        });

        this.recommendations = response.data.recommandations || [];
        // console.log(this.recommendations)
      } catch (error) {
        console.error('Erreur:', error);
        if (error.response?.status === 401) {
          console.error("Session expirée. Veuillez vous reconnecter.");
        } else {
          this.errorMessage = error.response?.data?.detail || 
            "Une erreur est survenue lors de la récupération des recommandations.";
        }
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    },
    formatFullDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getStatusText(status) {
      const statusMap = {
        'en_attente': "En attente",
        'en_cours': "En cours",
        'terminee': "Terminée",
        'non_commencee': "Non commencée"
      };
      return statusMap[status] || status;
    },
    showDetails(item) {
      this.selectedItem = item;
    },
    closeModal() {
      this.selectedItem = null;
    },
    printReport() {
      window.print();
    },
    applyFilters() {
      // Les computed properties se mettent à jour automatiquement
    },
    async markAsComplete(item) {
      try {
        if (!isAuthenticatedSession()) {
          this.$router.push('/connexion');
          return;
        }

        const recommandationId = item.recommandation.id;
        const apiUrl = `/api/actions-recommandations/${recommandationId}/`;

        await axios.patch(
          apiUrl,
          { statut: 'terminee' },
          {
            withCredentials: true
          }
        );

        // Actualiser les recommandations après la mise à jour
        await this.fetchRecommendations();
        // Optionnel: Afficher une notification de succès
        console.log(`Recommandation ${recommandationId} marquée comme terminée.`);

      } catch (error) {
        console.error("L'Auteur de la recommandation ne peut pas la modifier", error);
        if (error.response?.status === 401) {
          this.$router.push('/connexion');
        } else {
          this.errorMessage = error.response?.data?.detail || "Une erreur est survenue lors de la mise à jour du statut.";
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
